/**
 * UnitPrice — the per-unit money components.
 *
 * A **per-unit** money value (a unit price, a unit cost, a per-unit landed cost,
 * a per-diem rate) is `NUMERIC(18,4)`, while an **amount** (a line total, an
 * invoice balance, a payment) is `NUMERIC(18,2)` — the money type policy in
 * `docs/erp/modern-architecture-principles.md`. The two need different display
 * precision, and getting it wrong is a data-integrity bug in both directions: a
 * 4-dp value rendered at 2 dp shows a legitimate 0.0037 as "0.00" (reads as data
 * loss, and truncates on read-back in an editor), while padding every ordinary
 * 12.34 unit price to "12.3400" claims a precision the value does not have.
 *
 * `LocalizedMonetaryInput` / `LocalizedCurrencyDisplay` already implement the
 * rule — "up to 4, floored at the currency's minor unit" — behind
 * `decimalPlaces={4}`. What they could not do is make a call site *remember* it:
 * the policy lived in ~90 hand-copied props across twelve modules, so every new
 * unit-price field started life at the 2-dp amount default and each fix reached
 * only the surface someone happened to be looking at.
 *
 * These three components are the single preferred path. They carry the policy so
 * a call site cannot express the wrong precision for a per-unit value:
 *
 * - `UnitPriceInput`     — editing a per-unit value (wraps `LocalizedMonetaryInput`)
 * - `UnitPriceDisplay`   — reading one that carries its own `currency_code`
 * - `BaseUnitPriceDisplay` — reading one with no currency of its own, in the
 *                            reporting base currency (wraps `PreferredCurrencyDisplay`)
 *
 * Pick between the two display components exactly as you would between
 * `LocalizedCurrencyDisplay` and `PreferredCurrencyDisplay` — see
 * `docs/design-system/ui-i18n.md` § Currency.
 *
 * NOT for an **amount** (use the money components directly, unset), and not for
 * an **FX rate** — a rate is `DECIMAL(9,6)` on its own scale and is neither a
 * price nor per-unit. `tools/check_unit_price_component.py` enforces the split.
 */

import {
  LocalizedCurrencyDisplay,
  PreferredCurrencyDisplay,
  type LocalizedCurrencyDisplayProps,
  type PreferredCurrencyDisplayProps,
} from './LocalizedCurrencyDisplay';
import { LocalizedMonetaryInput, type LocalizedMonetaryInputProps } from './LocalizedMonetaryInput';

/** Per-unit money precision: up to 4 decimals, floored at the currency's minor unit. */
const PER_UNIT_DECIMAL_PLACES = 4;

export type UnitPriceInputProps = Omit<LocalizedMonetaryInputProps, 'decimalPlaces'>;

/**
 * Edit a per-unit money value. Same API as `LocalizedMonetaryInput` minus
 * `decimalPlaces`, which is fixed at the per-unit policy.
 *
 * Do not pass a hardcoded numeric `placeholder` ("0.00", "0.0000"): the digits
 * duplicate what the field's own currency prefix and label already say, and the
 * separator is wrong for every `dot_comma` locale.
 */
export function UnitPriceInput(props: UnitPriceInputProps) {
  // unit-price-precision-ok: this component IS the per-unit policy.
  return <LocalizedMonetaryInput {...props} decimalPlaces={PER_UNIT_DECIMAL_PLACES} />;
}

export type UnitPriceDisplayProps = Omit<LocalizedCurrencyDisplayProps, 'decimalPlaces'>;

/**
 * Read a per-unit money value that carries its own currency — pass the entity's
 * `currency_code` verbatim, including `null`.
 */
export function UnitPriceDisplay(props: UnitPriceDisplayProps) {
  // unit-price-precision-ok: this component IS the per-unit policy.
  return <LocalizedCurrencyDisplay {...props} decimalPlaces={PER_UNIT_DECIMAL_PLACES} />;
}

export type BaseUnitPriceDisplayProps = Omit<PreferredCurrencyDisplayProps, 'decimalPlaces'>;

/**
 * Read a per-unit money value with no currency of its own, in the reporting base
 * currency the backend computed it in (a per-unit aggregate on a report or KPI).
 */
export function BaseUnitPriceDisplay(props: BaseUnitPriceDisplayProps) {
  // unit-price-precision-ok: this component IS the per-unit policy.
  return <PreferredCurrencyDisplay {...props} decimalPlaces={PER_UNIT_DECIMAL_PLACES} />;
}
